import { useState } from "react";
import { taskService } from "@miplatform/api";
import {
  ASSIGNMENT_TEMPLATE_TYPES,
  ENTITY_NAMES,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IInspectionTemplate,
  IInspectionTemplateListItemResponse,
  IInspectionTemplatePayload,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getInspectionTemplateUUID = createUUID();

export const useInspectionTemplate = () => {
  const stateManager = useComponentState();
  const [inspectionTemplateDetails, setInspectionTemplateDetails] =
    useState<IInspectionTemplate | null>(null);

  const getInspectionTemplateDetails = (
    id: string,
    options?: { onSuccess?: (data: IInspectionTemplate) => void }
  ) => {
    stateManager.abort(getInspectionTemplateUUID);
    stateManager.run(async () => {
      const response = await taskService.inspectionTemplates.getById({ id });
      if (response.status === HTTP_STATUSES.OK) {
        setInspectionTemplateDetails(response.data);
        options?.onSuccess?.(response.data);
      }
    }, getInspectionTemplateUUID);
  };

  const createInspectionTemplate = (
    data: IInspectionTemplatePayload,
    options?: { onSuccess?: (data: IInspectionTemplate) => void }
  ) => {
    const requestData = {
      ...data,
      type: ASSIGNMENT_TEMPLATE_TYPES.INSPECTION,
    };
    return stateManager.run(async () => {
      const response = await taskService.inspectionTemplates.create({
        data: requestData,
        toastMessageConfig: {
          success: {
            text: `${data?.name} was created.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        options?.onSuccess?.(response.data);
      }
      return response;
    });
  };

  const updateInspectionTemplate = (
    id: string,
    data: IInspectionTemplatePayload,
    options?: { onSuccess?: (data: IInspectionTemplate) => void; name?: string }
  ) => {
    return stateManager.run(async () => {
      const response = await taskService.inspectionTemplates.update({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: `${
              options?.name ?? data.name ?? ENTITY_NAMES.INSPECTION_TEMPLATE
            } was updated.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        options?.onSuccess?.(response.data);
      }
      return response;
    });
  };

  const deleteInspectionTemplate = (
    data: IInspectionTemplateListItemResponse
  ) => {
    return stateManager.run(async () =>
      taskService.inspectionTemplates.delete({
        id: data.id,
        modifiedOn: data.modifiedOn,
        toastMessageConfig: {
          success: { text: `${data.name} was deleted successfully.` },
        },
      })
    );
  };

  return {
    createInspectionTemplate,
    updateInspectionTemplate,
    deleteInspectionTemplate,
    getInspectionTemplateDetails,
    inspectionTemplateDetails,
    loading: stateManager.isBusy(),
  };
};
